import { useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { ComplianceRequirement } from 'types/compliance';
import { OpenModalEventHandler, LOGIN_REGISTRATION_MODAL, TERMS_OF_USE_MODAL } from 'contexts';
import { LOCALE_MODAL_PATHS } from 'types/locales';

//#region Constants

export const DEFAULT_TIMEOUT_TIMING_DELAY = 300;

export const IMMEDIATE_TIMING = 'immediate' as const;
export const NEXT_TICK_TIMING = 'nextTick' as const;
export const ANIMATION_FRAME_TIMING = 'animationFrame' as const;
export const TIMEOUT_TIMING = 'timeout' as const;
//#endregion

//#region Types
export type TimingStrategy = typeof IMMEDIATE_TIMING | typeof NEXT_TICK_TIMING | typeof ANIMATION_FRAME_TIMING | typeof TIMEOUT_TIMING;

export type UseComplianceModalOptions = {
  complianceRequirement: ComplianceRequirement;
  providerId?: number | null;
  timing?: TimingStrategy;
  delay?: number;
  openModal: OpenModalEventHandler;
};

export type UseComplianceModalReturn = {
  openComplianceModal: () => void;
  clearComplianceModal: () => void;
};

export type UseReusableComplianceModalReturn = (complianceRequirement: ComplianceRequirement, providerId?: number | null) => void;

function clearTimeoutRef(timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>) {
  if (timeoutRef.current) {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }
}

/**
 * Custom hook for handling compliance-based modal opening with proper cleanup and safety.
 *
 * This hook provides a safer alternative to the useEffect pattern by:
 * - Centralizing the modal opening logic
 * - Providing manual control over when modals are opened
 * - Ensuring proper cleanup of timeouts
 * - Making the behavior more predictable and testable
 *
 * Timing Options:
 * - 'immediate' - Opens modal immediately (best for critical compliance)
 * - 'nextTick' - Opens on next tick using Promise.resolve() (good for React state updates)
 * - 'animationFrame' - Opens on next animation frame (smooth, prevents layout thrashing)
 * - 'timeout' - Uses setTimeout with delay (legacy behavior, not recommended)
 *
 * @param options - Configuration options for the compliance modal
 * @returns Object with methods to control the compliance modal
 */
export function useComplianceModal({
  complianceRequirement,
  providerId,
  openModal,
  delay = DEFAULT_TIMEOUT_TIMING_DELAY,
  timing = NEXT_TICK_TIMING,
}: UseComplianceModalOptions): UseComplianceModalReturn {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const openModalRef = useRef(openModal);

  // Update the ref when openModal changes
  useLayoutEffect(() => {
    openModalRef.current = openModal;
  }, [openModal]);


  const openComplianceModal = useCallback(() => {
    // Clear any existing timeout
    clearTimeoutRef(timeoutRef);

    // Only proceed if there's a compliance requirement
    if (complianceRequirement === ComplianceRequirement.NONE) {
      return;
    }

    const openModalAction = () => {
      try {
        if (complianceRequirement === ComplianceRequirement.SIGN_IN) {
          openModalRef.current?.(LOGIN_REGISTRATION_MODAL, {
            warningMessage: LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_FORM_SIGNIN_REQUIRED_WARNING,
          });
        } else if (complianceRequirement === ComplianceRequirement.TERMS_OF_USE) {
          openModalRef.current?.(TERMS_OF_USE_MODAL, { providerId });
        }
      } catch (error) {
        console.error('Error opening compliance modal:', error);
      } finally {
        timeoutRef.current = null;
      }
    };

    switch (timing) {
      case IMMEDIATE_TIMING:
        openModalAction();
        break;
      case NEXT_TICK_TIMING:
        Promise.resolve().then(openModalAction);
        break;
      case ANIMATION_FRAME_TIMING:
        requestAnimationFrame(openModalAction);
        break;
      case TIMEOUT_TIMING:
      default:
        timeoutRef.current = setTimeout(openModalAction, delay);
        break;
    }
  }, [complianceRequirement, providerId, delay, timing]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeoutRef(timeoutRef);
    };
  }, []);

  return {
    openComplianceModal,
    clearComplianceModal: () => clearTimeoutRef(timeoutRef),
  };
}

/**
 * Hook that automatically opens compliance modals based on requirements.
 * This is a convenience hook that combines the manual control with automatic triggering.
 *
 * @param options - Configuration options for the compliance modal
 * @returns Object with methods to control the compliance modal
 */
export function useAutoComplianceModal(options: UseComplianceModalOptions): UseComplianceModalReturn {
  const { openComplianceModal, clearComplianceModal } = useComplianceModal(options);
  const { complianceRequirement, providerId } = options;

  // Automatically open modal when compliance requirement changes
  // Use a ref to track if we've already opened the modal for this requirement
  const hasOpenedRef = useRef<string | null>(null);
  const openComplianceModalRef = useRef(openComplianceModal);

  useLayoutEffect(() => {
    openComplianceModalRef.current = openComplianceModal;
  }, [openComplianceModal]);

  useEffect(() => {
    const requirementKey = `${complianceRequirement}-${providerId}`;
    // Only open if we haven't already opened for this specific requirement
    if (complianceRequirement !== ComplianceRequirement.NONE && hasOpenedRef.current !== requirementKey) {
      hasOpenedRef.current = requirementKey;
      openComplianceModalRef?.current?.();
    }
  }, [complianceRequirement, providerId]);

  return {
    openComplianceModal,
    clearComplianceModal,
  };
}

/**
 * Hook that returns a reusable function for opening compliance modals immediately.
 * The returned function accepts compliance requirement and provider ID as parameters,
 * making it flexible for use across different components.
 *
 * @param openModal - The function to open modals
 * @returns Object with reusable openComplianceModal function
 *
 * @example
 * ```tsx
 * const { openComplianceModal } = useReusableComplianceModal(openModal);
 *
 * // Use it with different compliance requirements
 * const handleSignInClick = () => {
 *   openComplianceModal(ComplianceRequirement.SIGN_IN, providerId);
 * };
 *
 * const handleTermsClick = () => {
 *   openComplianceModal(ComplianceRequirement.TERMS_OF_USE, providerId);
 * };
 * ```
 */
export function useReusableComplianceModal(openModal: OpenModalEventHandler): UseReusableComplianceModalReturn {
  const openModalRef = useRef(openModal);

  // Update the ref when openModal changes
  useLayoutEffect(() => {
    openModalRef.current = openModal;
  }, [openModal]);

  const openComplianceModal = useCallback((complianceRequirement: ComplianceRequirement, providerId?: number | null) => {
    // Only proceed if there's a compliance requirement
    if (complianceRequirement === ComplianceRequirement.NONE) {
      return;
    }

    try {
      if (complianceRequirement === ComplianceRequirement.SIGN_IN) {
        openModalRef.current?.(LOGIN_REGISTRATION_MODAL, {
          warningMessage: LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_FORM_SIGNIN_REQUIRED_WARNING,
        });
      } else if (complianceRequirement === ComplianceRequirement.TERMS_OF_USE) {
        openModalRef.current?.(TERMS_OF_USE_MODAL, { providerId });
      }
    } catch (error) {
      console.error('Error opening compliance modal:', error);
    }
  }, []);

  return openComplianceModal;
}

